import dagre from 'dagre';
import { Node, Edge, Position } from 'reactflow';

import { CanvasNode, CanvasEdge } from '~/scenes/Build/scenes/FlowBuilder/stores/canvasStore';
import { filterByLevel } from '~/scenes/Build/scenes/FlowBuilder/stores/helpers/filterByLevel';
import { Track } from '~/scenes/Build/scenes/FlowBuilder/stores/trackStore';

const nodeWidth = 100;
const nodeHeight = 100;

type Elements = {
  nodes: Node[];
  edges: Edge[];
};

type CanvasElements = {
  nodes: Map<string, CanvasNode>;
  edges: Map<string, CanvasEdge>;
  currentLevelName?: string;
  offsetX?: number;
  offsetY?: number;
  tracks?: Map<string, Track>;
  stickToFirstNode?: boolean;
};

/**
 * Formats and positions React Flow nodes using the Dagre layout algorithm.
 *
 * @param elements - Object containing nodes and edges arrays. Defaults to empty arrays if not provided.
 * @param elements.nodes - Array of React Flow nodes to be positioned.
 * @param elements.edges - Array of React Flow edges connecting the nodes.
 * @param direction - Layout direction ('LR' for left-to-right or 'TB' for top-to-bottom). Defaults to 'LR'.
 * @returns Object containing the formatted nodes with calculated positions and the original edges.
 */
export const formatReactFlowNodesPositions = (
  { nodes, edges }: Elements = { nodes: [], edges: [] },
  direction = 'LR'
) => {
  const cleanNodes = nodes.map(node => ({
    id: node.id,
    data: {
      tags: node.data.tags
    },
    position: { x: 0, y: 0 },
    deletable: node.deletable
  }));
  const dagreGraph = new dagre.graphlib.Graph();
  const isHorizontal = direction === 'LR';

  dagreGraph.setDefaultEdgeLabel(() => ({})).setGraph({ rankdir: direction });

  cleanNodes.forEach(node => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach(edge => {
    const sourceNode = cleanNodes.find(node => node.id === edge.source);
    const isChoice = sourceNode?.data.tags.includes('choice');
    const isParallel = sourceNode?.data.tags.includes(
      'parallel-execution-connector'
    );

    dagreGraph.setEdge(edge.source, edge.target, {
      minlen: isChoice || isParallel ? 7 : 2
    });
  });

  dagre.layout(dagreGraph);

  cleanNodes.forEach(node => {
    const nodeWithPosition = dagreGraph.node(node.id);

    // @ts-expect-error
    node.targetPosition = isHorizontal ? Position.Left : Position.Top;
    // @ts-expect-error
    node.sourcePosition = isHorizontal ? Position.Right : Position.Bottom;
    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2
    };

    return node;
  });

  return { nodes: cleanNodes, edges };
};

/**
 * Format flow using Canvas Store nodes and edges
 *
 * This function applies the Dagre layout algorithm to Canvas Store entities,
 * calculating optimal positions for nodes based on their connections.
 * It filters nodes by the current level before formatting.
 *
 * @param nodes - Map of Canvas Store nodes
 * @param edges - Map of Canvas Store edges
 * @param currentLevelName - Current level name for filtering (optional, defaults to 'start')
 * @param direction - Layout direction ('LR' for left-to-right, 'TB' for top-to-bottom)
 * @returns Object with updated nodes Map (with new positions)
 */
export const formatCanvasNodesPositions = (
  {
    nodes,
    edges,
    currentLevelName,
    offsetX = 0,
    offsetY = 0,
    tracks,
    stickToFirstNode = false
  }: CanvasElements,
  direction: 'LR' | 'TB' = 'LR'
): { nodes: Map<string, CanvasNode>; edges: Map<string, CanvasEdge> } => {
  let nodesToFormat: Map<string, CanvasNode>;
  let edgesToFormat: Map<string, CanvasEdge>;
  if (currentLevelName) {
    const { nodes: filteredNodes, edges: filteredEdges } = filterByLevel(
      nodes,
      edges,
      currentLevelName,
      tracks
    );
    nodesToFormat = filteredNodes;
    edgesToFormat = filteredEdges;
  } else {
    nodesToFormat = new Map(nodes);
    edgesToFormat = new Map(edges);
  }

  const dagreGraph = new dagre.graphlib.Graph();

  dagreGraph.setDefaultEdgeLabel(() => ({})).setGraph({ rankdir: direction });

  nodesToFormat.forEach(node => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edgesToFormat.forEach(edge => {
    const sourceNode = nodesToFormat.get(edge.sourceId);
    const isChoice = sourceNode?.tags.includes('choice');
    const isParallel = sourceNode?.tags.includes(
      'parallel-execution-connector'
    );

    dagreGraph.setEdge(edge.sourceId, edge.targetId, {
      minlen: isChoice || isParallel ? 7 : 2
    });
  });

  dagre.layout(dagreGraph);

  const firstNode = nodesToFormat.values().next().value;
  let stickyOffsetX = 0;
  let stickyOffsetY = 0;

  if (stickToFirstNode && firstNode) {
    const firstNodeCalculatedPosition = dagreGraph.node(firstNode.id);
    const firstNodeCalculatedX =
      firstNodeCalculatedPosition.x - nodeWidth / 2 + offsetX;
    const firstNodeCalculatedY =
      firstNodeCalculatedPosition.y - nodeHeight / 2 + offsetY;

    stickyOffsetX = firstNode.position.x - firstNodeCalculatedX;
    stickyOffsetY = firstNode.position.y - firstNodeCalculatedY;
  }

  const updatedNodes = new Map<string, CanvasNode>(nodes);

  nodesToFormat.forEach(node => {
    const nodeWithPosition = dagreGraph.node(node.id);
    updatedNodes.set(node.id, {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2 + offsetX + stickyOffsetX,
        y: nodeWithPosition.y - nodeHeight / 2 + offsetY + stickyOffsetY
      }
    });
  });

  return { nodes: updatedNodes, edges };
};
